import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { getPrecisionRegex, integerRegex } from '../constants/regex.ts';
import type { RangeConfigurableFilter, RangeSelectorValue } from '../types.ts';

export const useRangeValidation = (config?: RangeConfigurableFilter<string>['config']) => {
  const isFloat = config?.inputType === 'float';
  const precision = config?.precision ?? 0;

  const precisionRegex = useMemo(() => getPrecisionRegex(precision), [precision]);

  const isValid = (val: string) => {
    return isFloat ? precisionRegex.test(val) : integerRegex.test(val);
  };

  const validate = (min: string, max: string, t: TFunction<'translation', undefined>): RangeSelectorValue => {
    const parsedMin = min ? parseFloat(min) : undefined;
    const parsedMax = max ? parseFloat(max) : undefined;

    const errorMap: RangeSelectorValue = {};

    if (min && (parsedMin === undefined || Number.isNaN(parsedMin) || !isValid(min))) {
      errorMap.min = t('Invalid number format');
    }

    if (max && (parsedMax === undefined || Number.isNaN(parsedMax) || !isValid(max))) {
      errorMap.max = t('Invalid number format');
    }

    if (
      !errorMap.min &&
      !errorMap.max &&
      parsedMin !== undefined &&
      parsedMax !== undefined &&
      !Number.isNaN(parsedMin) &&
      !Number.isNaN(parsedMax) &&
      parsedMin > parsedMax
    ) {
      errorMap.min = t('Min must be less than Max');
      errorMap.max = t('Max must be greater than Min');
    }

    const minValue = config?.detailedOptionsConfig?.minValue;
    const maxValue = config?.detailedOptionsConfig?.maxValue;

    for (const [key, value] of [
      ['min', parsedMin],
      ['max', parsedMax],
    ] as const) {
      if (!errorMap[key] && value !== undefined && !Number.isNaN(value) && minValue !== undefined && value < minValue) {
        errorMap[key] =
          key === 'min'
            ? t('Min must be greater than or equal to {minValue}', { minValue })
            : t('{field} must be greater than or equal to {min}', { field: t('Max'), min: minValue });
      }

      if (!errorMap[key] && value !== undefined && !Number.isNaN(value) && maxValue !== undefined && value > maxValue) {
        errorMap[key] =
          key === 'min'
            ? t('{field} must be less than or equal to {max}', { field: t('Min'), max: maxValue })
            : t('Max must be less than or equal to {maxValue}', { maxValue });
      }
    }
    return errorMap;
  };

  return { validate };
};
